import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { storeConfig } from '../../store.config'
import { ROUTES } from '../../src/constants/routes.constants'
import { Divider, StorefrontBackdrop, useFadeUp } from '../../src/ui/components/storefront/chrome'

/**
 * The brand story as a right-anchored slide-OVER sheet that OVERLAPS the home
 * page (the home stays visible, dimmed, on the left) when "Discover our story"
 * is clicked — not a scrolled section, not a full-screen replacement. Slides
 * in right-to-left (calm, ~0.7s); the background image + a frosted panel keep
 * all copy dark and legible. Controlled by the parent via `onClose`; render
 * inside <AnimatePresence> so the exit slide plays. Click the dim / ×/Esc to
 * close.
 */
export function StoryOverlay({ onClose }: { readonly onClose: () => void }): JSX.Element {
  const reduce = useReducedMotion() === true
  const fade = useFadeUp()
  const brand = storeConfig.brand
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [onClose])
  return (
    <motion.div
      className="fixed inset-0 z-50 flex justify-end bg-charcoal/40"
      role="dialog"
      aria-modal="true"
      aria-label="Our story"
      onClick={onClose}
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
      transition={{ duration: 0.4 }}
    >
      {/* The sheet — overlaps the home (left strip stays visible, dimmed). */}
      <motion.div
        className="relative h-full w-full max-w-2xl overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        variants={{ hidden: { x: reduce ? 0 : '100%' }, visible: { x: 0 } }}
        transition={{ duration: reduce ? 0.2 : 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <StorefrontBackdrop />

        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-border bg-bg-primary/70 text-charcoal backdrop-blur-md transition hover:text-forest sm:right-6 sm:top-6"
        >
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden>
            <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <div className="relative z-[1] flex min-h-full items-center justify-center px-5 py-16">
          <motion.article
            className="mx-auto max-w-xl rounded-[2rem] border border-border/40 bg-bg-primary/72 px-6 py-10 text-center font-body leading-relaxed text-charcoal shadow-lg backdrop-blur-md sm:px-12 sm:py-12"
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.12, delayChildren: reduce ? 0 : 0.35 } } }}
          >
            {brand.nameScript !== null ? (
              <motion.p
                variants={fade}
                className="font-kannada text-forest leading-none"
                style={{ fontSize: 'clamp(2rem, 7vw, 3.5rem)' }}
              >
                {brand.nameScript}
              </motion.p>
            ) : null}
            <motion.h2
              variants={fade}
              className="mt-4 font-heading text-charcoal leading-tight"
              style={{ fontSize: 'clamp(1.5rem, 4.5vw, 2.5rem)' }}
            >
              Before any cradle, there was the lap.
            </motion.h2>
            <motion.div variants={fade} className="my-6">
              <Divider />
            </motion.div>
            <motion.div variants={fade} className="space-y-5 text-charcoal" style={{ fontSize: 'clamp(0.95rem, 1.8vw, 1.1rem)' }}>
              <p>
                Before any lullaby, there was the rhythm of a mother&rsquo;s gently
                nodding thigh — the first cradle a child ever knows.
              </p>
              <p>
                For generations, Indian mothers have made their crossed legs that
                cradle: the quiet{' '}
                <span className="font-kannada text-forest">ಉಯ್ಯಮಡಿಲು</span>, rocking
                children to sleep across centuries.
              </p>
              <p>
                We bring that ritual home as a handcrafted rattan &amp; bamboo rocker —
                the same rhythm, the same closeness, for the moments her arms need a
                gentle helper.
              </p>
            </motion.div>
            <motion.p
              variants={fade}
              className="mt-8 font-heading italic text-charcoal"
              style={{ fontSize: 'clamp(1.125rem, 2.6vw, 1.6rem)' }}
            >
              {brand.tagline}
            </motion.p>
            <motion.div variants={fade} className="mt-9 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link
                to={ROUTES.ACCOUNTREGISTER}
                className="rounded-full bg-forest px-6 py-3 font-body text-body text-charcoal shadow-md transition hover:bg-forest/90 hover:shadow-lg"
              >
                Create an account
              </Link>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-border px-6 py-3 font-body text-body text-charcoal transition hover:border-forest hover:text-forest"
              >
                Close
              </button>
            </motion.div>
          </motion.article>
        </div>
      </motion.div>
    </motion.div>
  )
}
